import React from 'react';
import CreditCostPreview from '../../ui/CreditCostPreview';
import CustomSelect from '../../ui/CustomSelect';
import { ModeOption } from '../../../hooks/useModeRestrictions';
import { SubscriptionType } from '../../../services/subscriptionService';

interface CreditEstimation {
  creditCost: number | null;
  isEstimating: boolean;
  error: string | null;
  canAfford: boolean;
}

interface DesignerSummarySidebarProps {
  inputImageUrls: string[];
  garmentName?: string;
  garmentIcon?: string;
  attributes: Record<string, string[]>;
  mode: string;
  availableModes: ModeOption[];
  creditEstimation: CreditEstimation;
  onModeChange: (mode: string) => void;
  onGenerate: () => void;
  isProcessing: boolean;
  generationTime: number;
}

const DesignerSummarySidebar: React.FC<DesignerSummarySidebarProps> = ({
  inputImageUrls,
  garmentName,
  garmentIcon,
  attributes,
  mode,
  availableModes,
  creditEstimation,
  onModeChange,
  onGenerate,
  isProcessing,
  generationTime,
}) => {
  const attributeEntries = Object.entries(attributes).filter(([, values]) => values.length > 0);

  return (
    <div className="flex flex-col space-y-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      {/* Input images */}
      <div className="space-y-1.5">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Input</p>
        <div className="grid grid-cols-3 gap-2">
          {inputImageUrls.map((url, index) => (
            <div key={`${url}-${index}`} className="aspect-square overflow-hidden rounded-lg border border-gray-100 bg-white">
              <img src={url} alt={`Input ${index + 1}`} className="h-full w-full object-cover" />
            </div>
          ))}
        </div>
      </div>

      {/* Garment */}
      <div className="space-y-1.5">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Garment</p>
        <div className="flex items-center space-x-3 rounded-xl border border-gray-100 bg-white p-2.5">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border border-gray-50 bg-gray-50 text-lg">
            {garmentIcon || '✨'}
          </div>
          <p className="truncate text-xs font-semibold text-gray-900">{garmentName || 'Not selected'}</p>
        </div>
      </div>

      {/* Attributes */}
      {attributeEntries.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Attributes</p>
          <div className="flex flex-wrap gap-1">
            {attributeEntries.flatMap(([label, values]) =>
              values.map((value) => (
                <span
                  key={`${label}-${value}`}
                  className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-700"
                >
                  {value}
                </span>
              ))
            )}
          </div>
        </div>
      )}

      {/* Quality mode */}
      <div className="space-y-1.5">
        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Quality Mode</p>
        <CustomSelect
          value={mode}
          onChange={onModeChange}
          options={availableModes.map((option) => ({
            value: option.value,
            label: `${option.label}${
              option.requiresPlan?.[0] === SubscriptionType.PRO ? ' (Pro+)' : ''
            }`,
            disabled: option.disabled,
          }))}
          className="w-full rounded-xl border border-gray-200 bg-white px-2.5 py-2 text-xs font-semibold text-gray-900"
        />
      </div>

      {/* Generate */}
      <div className="mt-1 space-y-3 border-t border-gray-100 pt-3">
        <CreditCostPreview
          creditCost={creditEstimation.creditCost}
          isEstimating={creditEstimation.isEstimating}
          error={creditEstimation.error}
          canAfford={creditEstimation.canAfford}
        />
        <button
          type="button"
          onClick={onGenerate}
          disabled={isProcessing}
          className="flex w-full items-center justify-center space-x-2 rounded-xl bg-secondary-600 py-3 font-bold text-white transition-colors hover:bg-secondary-700 disabled:bg-secondary-400"
        >
          {isProcessing ? (
            <>
              <span className="h-5 w-5 animate-spin rounded-full border-b-2 border-white" />
              <span className="font-mono text-sm">{generationTime.toFixed(1)}s</span>
            </>
          ) : (
            <>
              <i className="fas fa-wand-magic-sparkles text-sm" />
              <span>Generate</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default DesignerSummarySidebar;
